import React from 'react'
import useSWR from 'swr'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { getClientById, removeClientById } from '../../api'
import ClientForm from '../../components/clients/form'
import { parseClientToForm } from '../../lib/parsers'
import ClientBalance from '../../components/clients/balance'
import useSWRMutation from 'swr/mutation'
import { useAlerts } from '../../context/alerts'
import Button, { ButtonSize, ButtonVariant } from '../../components/ui/button'
import { usePageHeader } from '../../context/pageHeader'
import { ReactComponent as EditIcon } from '../../static/images/edit.svg'
import { ReactComponent as TrashIcon } from '../../static/images/trash.svg'
import { clearNewInvoiceDraftStorage } from '../../lib/invoiceDraftStorage'
import Spinner from '../../components/ui/spinner'

const EditClientPage = () => {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const focusEmail = searchParams.get('focus') === 'email'
  const { data, error, isLoading } = useSWR(`/api/clients/${id}`, getClientById)
  const { addAlert } = useAlerts()
  const { setPageHeader, clearPageHeader } = usePageHeader()
  const {
    trigger: triggerDeleteClient,
    isMutating: isDeletingClient,
    error: deleteClientError,
  } = useSWRMutation('/api/clients', removeClientById)

  const navigate = useNavigate()
  const [isEditing, setIsEditing] = React.useState(false)

  const handleCreateInvoiceForClient = React.useCallback(() => {
    clearNewInvoiceDraftStorage()
    navigate(`/invoices/new?clientId=${id}`, { state: { discardDraft: Date.now() } })
  }, [navigate, id])

  React.useEffect(() => {
    setPageHeader({
      title: data?.tradingName || 'Client',
      titleClassName: 'master-page-header__title--ellipsis-300',
      actions: (
        <Button variant={ButtonVariant.PRIMARY} onClick={handleCreateInvoiceForClient}>
          Create invoice for this client
        </Button>
      ),
    })

    return () => clearPageHeader()
  }, [setPageHeader, clearPageHeader, data?.tradingName, handleCreateInvoiceForClient])

  if(isLoading) return <div className='text-center w-full'><Spinner message='Loading client'/></div>

  if(!data || error){
    return <div>Error ocurred.</div>
  }
  if(id === undefined) return <div>Client id is required</div>

  const onSave = async () => {
    navigate(-1)
  }

  const toDisplayValue = (value?: string | null) => {
    const text = String(value ?? '').trim()
    return text !== '' ? text : '—'
  }

  const fullName = [data?.name, data?.lastName]
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(' ')

  const handleDelete = async () => {
    if(id && id?.toString().trim() !== '') {
      if (window.confirm(`Are you sure you want to delete: "${data.tradingName}"`) === true) {
        await triggerDeleteClient({ params: { id } })
        addAlert({ type: 'info', message: 'Client deleted' })
        navigate('/dashboard', { replace: true })
      }
    }
  }

  return (
    <>
      {!isEditing ? (
        <div className='card border-0 p-4 bg-white'>
          <div className='row g-3'>
            <div className='col-12 col-md-6'>
              <small className='text-body-secondary d-block'>Trading name</small>
              <div className='fw-medium'>{toDisplayValue(data?.tradingName)}</div>
            </div>
            <div className='col-12 col-md-6'>
              <small className='text-body-secondary d-block'>Email</small>
              <div>{toDisplayValue(data?.email)}</div>
            </div>
            <div className='col-12 col-md-6'>
              <small className='text-body-secondary d-block'>Contact</small>
              <div>{toDisplayValue(fullName)}</div>
            </div>
            <div className='col-12 col-md-6'>
              <small className='text-body-secondary d-block'>Billing address</small>
              <div>{toDisplayValue(data?.billingAddress)}</div>
            </div>
          </div>
          <div className='mt-3'>
            <Button
              type='button'
              icon={<EditIcon width={14} />}
              iconPosition='start'
              variant={ButtonVariant.LIGHT}
              size={ButtonSize.SMALL}
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          </div>
        </div>
      ) : (
        <ClientForm
          defaultValues={parseClientToForm(data)}
          initialFocus={focusEmail ? 'email' : 'tradingName'}
          onSave={onSave}
          onCancel={() => setIsEditing(false)}
        />
      )}
      <div className='mt-5' />
      <ClientBalance />
      <div className='alert alert-danger mt-5'>
        <b>Danger Zone</b>
        <hr />
        <div className='d-flex align-items-center flex-wrap gap-4'>
          <span>Delete this client and all its invoices, this action cannot be undone.</span>
          <Button icon={<TrashIcon width={20} height={20} />} iconPosition='start' size={ButtonSize.SMALL} variant={ButtonVariant.LIGHT} disabled={isDeletingClient} onClick={handleDelete}>Delete client</Button>
        </div>
        {deleteClientError && <div>{deleteClientError.toString()}</div>}
      </div>
    </>
  )
}

export default EditClientPage
